// Surveys 2.0 logic evaluator (spec §2 "Logic evaluator"). Pure — no imports
// beyond the shared types. The Server keeps the same evaluator in
// src/api/engagement/engagement.survey/v2/logic.ts; keep both behaviourally
// identical: first matching jump wins, else `next`, else the next block in
// order, else the first ending.
import type {
  SurveyAnswerValue,
  SurveyBlock,
  SurveyCondition,
  SurveyDefinition,
} from "./surveyTypes";

export interface SurveyLogicContext {
  answers: Record<string, SurveyAnswerValue | undefined>;
  contact?: Record<string, any>;
  fields?: Record<string, any>;
  url?: string;
}

const isEmpty = (value: unknown) =>
  value === undefined ||
  value === null ||
  (typeof value === "string" && value.trim() === "") ||
  (Array.isArray(value) && value.length === 0);

/** Yes/no answers are booleans; editors may store 'yes'/'no' or 'true'/'false'. */
const normalize = (value: unknown): unknown => {
  if (typeof value === "string") {
    const lower = value.trim().toLowerCase();
    if (lower === "yes" || lower === "true") return true;
    if (lower === "no" || lower === "false") return false;
    if (lower !== "" && !isNaN(Number(lower))) return Number(lower);
    return lower;
  }
  return value;
};

const looselyEqual = (a: unknown, b: unknown) => {
  if (a && typeof a === "object" && !Array.isArray(a)) {
    // { other: '...' } matches the pseudo choice id "other".
    return normalize(b) === "other";
  }
  if (Array.isArray(a)) {
    return a.some((item) => normalize(item) === normalize(b));
  }
  return normalize(a) === normalize(b);
};

export const evaluateCondition = (
  condition: SurveyCondition,
  answer: SurveyAnswerValue | undefined,
  ctx: SurveyLogicContext,
): boolean => {
  try {
    if (condition.src === "answer") {
      switch (condition.op) {
        case "answered":
          return !isEmpty(answer);
        case "skipped":
          return isEmpty(answer);
        case "eq":
          return !isEmpty(answer) && looselyEqual(answer, condition.value);
        case "neq":
          return isEmpty(answer) || !looselyEqual(answer, condition.value);
        case "between": {
          const n = typeof answer === "number" ? answer : Number(answer);
          if (isEmpty(answer) || isNaN(n)) return false;
          const [min, max] = condition.value;
          return n >= Math.min(min, max) && n <= Math.max(min, max);
        }
        case "includes":
          if (Array.isArray(answer)) {
            return answer.includes(condition.value);
          }
          if (answer && typeof answer === "object") {
            const choices = (answer as any).choices;
            return (
              (Array.isArray(choices) && choices.includes(condition.value)) ||
              condition.value === "other"
            );
          }
          return answer === condition.value;
      }
      return false;
    }

    if (condition.src === "contact" || condition.src === "field") {
      const source =
        condition.src === "contact" ? ctx.contact ?? {} : ctx.fields ?? {};
      const actual = source[condition.key];
      switch (condition.op) {
        case "exists":
          return !isEmpty(actual);
        case "eq":
          return !isEmpty(actual) && looselyEqual(actual, condition.value);
        case "neq":
          return isEmpty(actual) || !looselyEqual(actual, condition.value);
        case "contains":
          return (
            !isEmpty(actual) &&
            String(actual)
              .toLowerCase()
              .includes(String(condition.value ?? "").toLowerCase())
          );
      }
      return false;
    }

    if (condition.src === "url") {
      const url = (ctx.url ?? "").toLowerCase();
      const value = String(condition.value ?? "").toLowerCase();
      return condition.op === "eq" ? url === value : url.includes(value);
    }
  } catch (exp) {}
  return false;
};

const targetExists = (def: SurveyDefinition, id: string | null | undefined) =>
  !!id &&
  (def.blocks.some((b) => b.id === id) || def.endings.some((e) => e.id === id));

/**
 * Where to go after `block` was answered with `answer`. Returns a block id or
 * an ending id (never null while the definition has at least one ending).
 */
export const nextBlockId = (
  def: SurveyDefinition,
  block: SurveyBlock,
  answer: SurveyAnswerValue | undefined,
  ctx: SurveyLogicContext,
): string => {
  for (const jump of block.jumps ?? []) {
    if (
      jump &&
      jump.if &&
      targetExists(def, jump.go) &&
      evaluateCondition(jump.if, answer, ctx)
    ) {
      return jump.go;
    }
  }

  if (block.next && targetExists(def, block.next)) {
    return block.next;
  }

  const index = def.blocks.findIndex((b) => b.id === block.id);
  if (index >= 0 && index < def.blocks.length - 1) {
    return def.blocks[index + 1].id;
  }

  return def.endings[0]?.id ?? "";
};

export const isEndingId = (def: SurveyDefinition, id: string | undefined) =>
  !!id && def.endings.some((e) => e.id === id);

/**
 * Number of questions on the longest possible path from `fromId` to an ending
 * (inclusive of `fromId`). Logic only jumps forward (publish validation), so a
 * memoised DFS over the block graph terminates; a visited set guards drafts
 * that still contain loops. Questions `isSkipped` says are not shown
 * ("Skip when known") count 0 but their routes are followed.
 */
export const longestRemaining = (
  def: SurveyDefinition,
  fromId: string,
  isSkipped?: (blockId: string) => boolean,
): number => {
  const memo = new Map<string, number>();
  const visiting = new Set<string>();

  const walk = (id: string): number => {
    const index = def.blocks.findIndex((b) => b.id === id);
    if (index < 0) return 0; // ending or unknown
    if (memo.has(id)) return memo.get(id)!;
    if (visiting.has(id)) return 0;
    visiting.add(id);

    const block = def.blocks[index];
    const targets = new Set<string>();
    for (const jump of block.jumps ?? []) {
      if (jump?.go) targets.add(jump.go);
    }
    if (block.next) {
      targets.add(block.next);
    } else if (index < def.blocks.length - 1) {
      targets.add(def.blocks[index + 1].id);
    }

    let best = 0;
    targets.forEach((target) => {
      best = Math.max(best, walk(target));
    });

    visiting.delete(id);
    const result = (isSkipped?.(id) ? 0 : 1) + best;
    memo.set(id, result);
    return result;
  };

  return walk(fromId);
};

/**
 * Progress 0..1 for a respondent who answered `answeredCount` questions and is
 * now on `currentId`: (answered + ½ for the open question) / (answered +
 * longest remaining path). The half step keeps the bar from sitting at an
 * empty 0% on the first question (the mock shows ~12% there) and it reaches 1
 * on an ending.
 */
export const progressEstimate = (
  def: SurveyDefinition,
  currentId: string,
  answeredCount: number,
  isSkipped?: (blockId: string) => boolean,
): number => {
  if (isEndingId(def, currentId)) return 1;
  const remaining = longestRemaining(def, currentId, isSkipped);
  const total = answeredCount + remaining;
  if (total <= 0) return 0;
  return Math.max(0, Math.min(1, (answeredCount + 0.5) / total));
};
